import { NextFunction, Request, Response } from "express";

import { User } from "../models";
import {
  extractToken,
  JWTPayload,
  verifyAccessToken,
} from "../utils/jwt-utils";
import { logger } from "../utils/logger";

// Extended Request interface to include user data
export interface AuthenticatedRequest extends Request {
  user: JWTPayload;
}

// Response interface for consistency
interface APIResponse {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * JWT Authentication Middleware
 * Validates JWT token and attaches user data to request
 */
export const authenticate = async (
  req: Request,
  res: Response,
  next: NextFunction,
): Promise<void> => {
  try {
    // Extract token from Authorization header
    const authHeader = req.headers.authorization;
    let token: string;

    try {
      token = extractToken(authHeader);
    } catch (error) {
      res.status(401).json({
        success: false,
        message: "Authorization token required",
        error:
          error instanceof Error
            ? error.message
            : "Invalid authorization header",
      } as APIResponse);
      return;
    }

    // Verify JWT token
    let decoded: JWTPayload;
    try {
      decoded = verifyAccessToken(token);
    } catch (error) {
      // Check if this is a profile-related request
      const isProfileRequest = req.path.includes("/profile");

      res.status(401).json({
        success: false,
        message: isProfileRequest
          ? "Unable to create the profile."
          : "Invalid or expired token",
        error:
          error instanceof Error ? error.message : "Token verification failed",
      } as APIResponse);
      return;
    }

    // Optional: Verify user still exists and is active
    // This adds a database call but ensures real-time user status validation
    const user = await User.findById(decoded.userId);
    if (!user) {
      res.status(401).json({
        success: false,
        message: "User not found",
      } as APIResponse);
      return;
    }

    if (!user.canLogin()) {
      res.status(403).json({
        success: false,
        message: `Account is ${user.status}. Please contact support.`,
      } as APIResponse);
      return;
    }

    // Update decoded data with fresh user status
    decoded.status = user.status;
    decoded.isEmailVerified = user.isEmailVerified;
    decoded.isProfileCompleted = user.isProfileCompleted;

    // Attach user data to request
    (req as AuthenticatedRequest).user = decoded;

    next();
  } catch (error) {
    logger.error("Authentication middleware error:", error);
    res.status(500).json({
      success: false,
      message: "Authentication failed. Please try again.",
    } as APIResponse);
  }
};

/**
 * Role-based authorization middleware
 * Checks if user has required role(s)
 */
export const authorize = (...roles: string[]) => {
  return (req: Request, res: Response, next: NextFunction): void => {
    try {
      const user = (req as AuthenticatedRequest).user;

      if (!user) {
        res.status(401).json({
          success: false,
          message: "Authentication required",
        } as APIResponse);
        return;
      }

      // Check if user has any of the required roles
      if (!roles.includes(user.role)) {
        logger.warn(
          `Unauthorized access attempt: ${user.email} (${user.role}) tried to access ${req.method} ${req.originalUrl}`,
        );
        res.status(403).json({
          success: false,
          message: "Insufficient permissions",
        } as APIResponse);
        return;
      }

      next();
    } catch (error) {
      logger.error("Authorization middleware error:", error);
      res.status(500).json({
        success: false,
        message: "Authorization failed. Please try again.",
      } as APIResponse);
    }
  };
};

/**
 * Email verification check middleware
 * Ensures user has verified their email
 */
export const requireEmailVerification = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  try {
    const user = (req as AuthenticatedRequest).user;

    if (!user) {
      res.status(401).json({
        success: false,
        message: "Authentication required",
      } as APIResponse);
      return;
    }

    if (!user.isEmailVerified) {
      res.status(403).json({
        success: false,
        message: "Email verification required",
        data: { requiresEmailVerification: true },
      } as APIResponse);
      return;
    }

    next();
  } catch (error) {
    logger.error("Email verification middleware error:", error);
    res.status(500).json({
      success: false,
      message: "Verification check failed. Please try again.",
    } as APIResponse);
  }
};

/**
 * Profile completion check middleware
 * Ensures user has completed their profile
 */
export const requireProfileCompletion = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  try {
    const user = (req as AuthenticatedRequest).user;

    if (!user) {
      res.status(401).json({
        success: false,
        message: "Authentication required",
      } as APIResponse);
      return;
    }

    if (!user.isProfileCompleted) {
      res.status(403).json({
        success: false,
        message: "Profile completion required",
        data: { requiresProfileCompletion: true },
      } as APIResponse);
      return;
    }

    next();
  } catch (error) {
    logger.error("Profile completion middleware error:", error);
    res.status(500).json({
      success: false,
      message: "Profile check failed. Please try again.",
    } as APIResponse);
  }
};

/**
 * Admin only middleware
 * Shortcut for admin role authorization
 */
export const adminOnly = authorize("admin");

/**
 * School admin middleware
 * Allows only school role users
 */
export const schoolOnly = authorize("school");

/**
 * Teacher middleware
 * Allows only teacher role users
 */
export const teacherOnly = authorize("teacher");

/**
 * Student middleware
 * Allows only student role users
 */
export const studentOnly = authorize("student");

/**
 * School staff middleware
 * Allows school admins and teachers
 */
export const schoolStaff = authorize("school", "teacher");

/**
 * Academic users middleware
 * Allows teachers and students
 */
export const academicUsers = authorize("teacher", "student");

/**
 * All authenticated users middleware
 * Allows all roles (just checks authentication)
 */
export const allUsers = authorize("admin", "school", "teacher", "student");

/**
 * Optional authentication middleware
 * Attaches user data if token is provided but doesn't require it
 */
export const optionalAuth = async (
  req: Request,
  res: Response,
  next: NextFunction,
): Promise<void> => {
  try {
    const authHeader = req.headers.authorization;

    // If no auth header, continue without user data
    if (!authHeader) {
      next();
      return;
    }

    try {
      const token = extractToken(authHeader);
      const decoded = verifyAccessToken(token);

      // Verify user still exists
      const user = await User.findById(decoded.userId);
      if (user?.canLogin()) {
        // Update decoded data with fresh user status
        decoded.status = user.status;
        decoded.isEmailVerified = user.isEmailVerified;
        decoded.isProfileCompleted = user.isProfileCompleted;

        (req as AuthenticatedRequest).user = decoded;
      }
    } catch (error) {
      // Ignore token errors for optional auth
      logger.debug("Optional auth token error (ignored):", error);
    }

    next();
  } catch (error) {
    logger.error("Optional authentication middleware error:", error);
    // Don't fail for optional auth
    next();
  }
};

/**
 * Active user middleware
 * Ensures user account is active (not suspended, banned, etc.)
 */
export const requireActiveUser = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  try {
    const user = (req as AuthenticatedRequest).user;

    if (!user) {
      res.status(401).json({
        success: false,
        message: "Authentication required",
      } as APIResponse);
      return;
    }

    const allowedStatuses = [
      "active",
      "pending_school_approval",
      "pending_admin_approval",
    ];

    if (!allowedStatuses.includes(user.status)) {
      res.status(403).json({
        success: false,
        message: `Account is ${user.status}. Please contact support.`,
      } as APIResponse);
      return;
    }

    next();
  } catch (error) {
    logger.error("Active user middleware error:", error);
    res.status(500).json({
      success: false,
      message: "User status check failed. Please try again.",
    } as APIResponse);
  }
};

/**
 * Rate limiting check middleware
 * Basic rate limiting based on user or IP
 * Controlled by ENABLE_RATE_LIMITING environment variable (defaults to false/disabled)
 */
export const rateLimit = (maxRequests: number, windowMs: number) => {
  const requests = new Map<string, { count: number; resetTime: number }>();
  // Rate limiting is disabled by default, only enabled if explicitly set to true
  const isRateLimitingEnabled = process.env.ENABLE_RATE_LIMITING === "true";

  return (req: Request, res: Response, next: NextFunction): void => {
    try {
      // Skip rate limiting if disabled (default behavior)
      if (!isRateLimitingEnabled) {
        logger.debug(
          "Rate limiting disabled via ENABLE_RATE_LIMITING environment variable",
        );
        next();
        return;
      }

      // Use user ID if authenticated, otherwise use IP
      const user = (req as AuthenticatedRequest).user;
      const identifier = user.userId || req.ip || "unknown";
      const now = Date.now();

      // Clean up expired entries
      for (const [key, value] of requests.entries()) {
        if (now > value.resetTime) {
          requests.delete(key);
        }
      }

      // Get or create rate limit data for this identifier
      let rateLimitData = requests.get(identifier);
      if (!rateLimitData || now > rateLimitData.resetTime) {
        rateLimitData = {
          count: 0,
          resetTime: now + windowMs,
        };
        requests.set(identifier, rateLimitData);
      }

      // Check if limit exceeded
      if (rateLimitData.count >= maxRequests) {
        const resetIn = Math.ceil((rateLimitData.resetTime - now) / 1000);
        logger.warn(
          `Rate limit exceeded for ${identifier}: ${rateLimitData.count}/${maxRequests} requests`,
        );
        res.status(429).json({
          success: false,
          message: `Too many requests. Try again in ${resetIn} seconds.`,
          data: {
            retryAfter: resetIn,
            limit: maxRequests,
            windowMs,
          },
        } as APIResponse);
        return;
      }

      // Increment request count
      rateLimitData.count++;
      requests.set(identifier, rateLimitData);

      // Add rate limit headers
      res.setHeader("X-RateLimit-Limit", maxRequests);
      res.setHeader("X-RateLimit-Remaining", maxRequests - rateLimitData.count);
      res.setHeader(
        "X-RateLimit-Reset",
        Math.ceil(rateLimitData.resetTime / 1000),
      );

      next();
    } catch (error) {
      logger.error("Rate limit middleware error:", error);
      // Don't block request on rate limit errors
      next();
    }
  };
};

/**
 * Create environment-aware rate limiting configurations
 * Rate limiting is disabled by default (ENABLE_RATE_LIMITING=false)
 * Set ENABLE_RATE_LIMITING=true in .env to enable rate limiting
 */
export const createRateLimit = {
  // Authentication endpoints (login, register, social login) - most critical
  auth: () => rateLimit(20, 15 * 60 * 1000), // 20 requests per 15 minutes

  // General endpoints (password reset, token refresh, etc.)
  general: () => rateLimit(50, 5 * 60 * 1000), // 50 requests per 5 minutes

  // OTP endpoints (send OTP, resend OTP) - more restrictive
  otp: () => rateLimit(10, 10 * 60 * 1000), // 10 requests per 10 minutes

  // Validation endpoints (email check, etc.) - higher limits
  validation: () => rateLimit(100, 5 * 60 * 1000), // 100 requests per 5 minutes

  // Fix: Upload endpoints (profile creation with files) - restricted due to file size
  upload: () => rateLimit(20, 10 * 60 * 1000), // 20 requests per 10 minutes

  // Guest endpoints (public school data) - generous limits for public access
  guest: () => rateLimit(200, 5 * 60 * 1000), // 200 requests per 5 minutes
};
